import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { SalaryAdvanceStatus } from "@/modules/hr/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import { useSalaryAdvanceListQuery } from "../api/queries";

const ALL = "__all__";

const STATUS_LABELS: Record<SalaryAdvanceStatus, string> = {
  pending: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
  fully_repaid: "ຫັກຄືນຄົບ",
};

function statusVariant(
  s: SalaryAdvanceStatus,
): "default" | "destructive" | "secondary" | "outline" {
  if (s === "approved" || s === "paid" || s === "fully_repaid")
    return "default";
  if (s === "rejected" || s === "cancelled") return "destructive";
  return "secondary";
}

export function SalaryAdvanceListPage() {
  const navigate = useNavigate({ from: "/app/hr/salary-advance/requests" });
  const search = useSearch({ from: "/app/hr/salary-advance/requests" }) as {
    offset?: number;
    limit?: number;
    status?: SalaryAdvanceStatus;
  };

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;

  const query = useSalaryAdvanceListQuery({
    offset,
    limit,
    status: search.status,
  });

  const items = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">
              ຄຳຂໍເບິກເງິນເດືອນລ່ວງໜ້າ
            </h1>
            <p className="mt-1 text-muted-foreground">
              ລາຍການຄຳຂໍ ແລະ ສະຖານະການດຳເນີນງານ
            </p>
          </div>
          <Button
            onClick={() =>
              navigate({ to: "/app/hr/salary-advance/requests/new" })
            }
            className="bg-black text-white hover:bg-black/90"
          >
            <PlusIcon className="size-4" />
            ຍື່ນຄຳຂໍໃໝ່
          </Button>
        </div>

        {/* Filter */}
        <div className="mb-4 flex items-center gap-3">
          <Select
            value={search.status ?? ALL}
            onValueChange={(v) =>
              navigate({
                search: {
                  offset: 0,
                  limit,
                  status: v === ALL ? undefined : (v as SalaryAdvanceStatus),
                },
              })
            }
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="ທຸກສະຖານະ" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>ທຸກສະຖານະ</SelectItem>
              <SelectItem value="pending">ລໍຖ້າອະນຸມັດ</SelectItem>
              <SelectItem value="approved">ອະນຸມັດແລ້ວ</SelectItem>
              <SelectItem value="rejected">ປະຕິເສດ</SelectItem>
              <SelectItem value="paid">ຈ່າຍແລ້ວ</SelectItem>
              <SelectItem value="cancelled">ຍົກເລີກ</SelectItem>
              <SelectItem value="fully_repaid">ຫັກຄືນຄົບ</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {query.isLoading ? (
          <div className="space-y-3">
            {(["a", "b", "c", "d"] as const).map((k) => (
              <div key={k} className="h-20 animate-pulse rounded-xl bg-muted" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed py-16 text-center text-muted-foreground">
            ຍັງບໍ່ມີຄຳຂໍ
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() =>
                  navigate({
                    to: "/app/hr/salary-advance/requests/$id",
                    params: { id: item.id },
                  })
                }
                className="w-full rounded-xl border bg-card px-5 py-4 text-left shadow-sm transition-colors hover:bg-accent/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium">
                      {Number(item.amount).toLocaleString()} ກີບ
                    </p>
                    <p className="mt-0.5 text-muted-foreground text-sm">
                      ຍື່ນວັນທີ {formatDateLocal(item.requestDate)}
                    </p>
                    {item.reason ? (
                      <p className="mt-1 line-clamp-1 text-muted-foreground text-xs">
                        {item.reason}
                      </p>
                    ) : null}
                  </div>
                  <Badge
                    variant={statusVariant(item.status)}
                    className="shrink-0"
                  >
                    {STATUS_LABELS[item.status] ?? item.status}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() =>
                navigate({
                  search: { ...search, offset: Math.max(0, offset - limit) },
                })
              }
            >
              ກ່ອນໜ້າ
            </Button>
            <span className="text-muted-foreground text-sm">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() =>
                navigate({ search: { ...search, offset: offset + limit } })
              }
            >
              ຖັດໄປ
            </Button>
          </div>
        )}
      </Main>
    </>
  );
}
